import { useTranslate } from '@tolgee/react';
import { ReturnActionsProps } from './types';
import { Button, Popconfirm, Tooltip, message } from 'antd';
import { confirmReturn, returnReviewResolve } from 'services/api/return';
import TikTokRefuse from 'components/Refund/TikTokRefuse';
import { useRequest } from 'ahooks';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import { useMemo } from 'react';
import { TikTokConfirmChange } from 'components/Refund/TikTokConfirmChange';
import { FakeRefuseButton } from 'components/Refund/FakeRefuse';
import { toUpperCase } from 'utils/toLowerCase';
import StatusButton from 'components/StatusButton';
import { formatNumber } from 'utils/numberFormat';
import OfferPartialRefund from '../OfferPartialRefund';

export const ReturnActionTikTok = (props: ReturnActionsProps) => {
  const {
    row: {
      region,
      unit,
      refundAmount,
      internalStoreId,
      externalStoreId,
      orderSn,
      returnSn,
      operate = [],
      dueDateMs,
    },
    platform,
    refreshList,
  } = props;
  const { t } = useTranslate();

  const agreeOnlyRefundOperate = useMemo(() => {
    return operate.find((item) => item === 'FULL_REFUND' || item === 'CONFIRM_ONLY_REFUND');
  }, [operate]);

  const refuseOperate = useMemo(() => {
    return operate.find((item) => item === 'REFUSE' || item === 'REFUSE_CHANGE');
  }, [operate]);

  const { loading: confirmLoading, runAsync: confirmSubmit } = useRequest(
    () =>
      confirmReturn({
        externalStoreId,
        externalTradeId: orderSn,
        returnSn,
        externalType: platform,
        operate: agreeOnlyRefundOperate, // 新版本使用FULL_REFUND，不能使用CONFIRM， 仅区分TIKTOK
      }),
    {
      manual: true,
      onSuccess() {
        refreshList();
      },
      onError(err: any) {
        message.error(getApiErrorCodeMsg(err.response));
      },
    },
  );

  const { loading: confirmReturnLoading, runAsync: confirmReturnRefundSubmit } = useRequest(
    () =>
      confirmReturn({
        externalStoreId,
        externalTradeId: orderSn,
        returnSn,
        externalType: platform,
        operate: 'CONFIRM_RETURN_REFUND',
      }),
    {
      manual: true,
      onSuccess() {
        refreshList();
      },
      onError(err: any) {
        message.error(getApiErrorCodeMsg(err.response));
      },
    },
  );

  return (
    <>
      {operate.includes('CONFIRM_RETURN_REFUND') && (
        <Popconfirm
          title={t('ViewRefund.确认同意买家的退货退款申请吗？')}
          onConfirm={confirmReturnRefundSubmit}
          okButtonProps={{ loading: confirmReturnLoading }}
        >
          <Button type="link">{t('btn_agree_refunds')}</Button>
        </Popconfirm>
      )}
      {operate.includes('CONFIRM_CHANGE') && (
        <TikTokConfirmChange
          externalStoreId={externalStoreId}
          orderSn={orderSn}
          returnSn={returnSn}
          platform={platform}
          refreshList={refreshList}
        />
      )}
      {refuseOperate && (
        <TikTokRefuse
          operate={refuseOperate}
          platform={platform}
          region={toUpperCase(region)}
          externalStoreId={externalStoreId}
          internalStoreId={internalStoreId}
          returnSn={returnSn}
          btnProps={{ type: 'link' }}
          onRefused={refreshList}
          dueDateMs={dueDateMs}
        />
      )}
      {operate.includes('FAKE_REFUSE') && <FakeRefuseButton />}
      {operate.includes('FAKE_REFUSE_1') && (
        <FakeRefuseButton
          title={t(
            dueDateMs && dueDateMs !== '0' && Number(dueDateMs) < Date.now()
              ? '当前退单已超过处理截止时间，无法进行拒绝操作'
              : '根据Tiktok政策，该退单申请不支持拒绝，如需拒绝请前往卖家中心联系平台客服',
          )}
        />
      )}
      {operate.includes('RETURN_REVIEW_OK') && (
        <StatusButton
          size="small"
          onClick={() =>
            returnReviewResolve(platform, returnSn)
              .then(() => {
                refreshList?.();
                message.success(t('操作成功'));
              })
              .catch(() => {
                message.error(t('translation.操作失败，请稍后再试'));
              })
          }
        >
          {t('确认收到包裹')}
        </StatusButton>
      )}
      {agreeOnlyRefundOperate && (
        <Popconfirm
          title={t('trade.全额退款提示', {
            money: `${unit}${formatNumber(refundAmount, '0,0.00')}`,
          })}
          onConfirm={confirmSubmit}
          okButtonProps={{ loading: confirmLoading }}
        >
          <Button type="link">{t('btn_agree_only_refund')}</Button>
        </Popconfirm>
      )}
      {operate.includes('ADD_EVIDENCE') && (
        <Tooltip title={t('Tiktok接口尚不支持该操作，请前往卖家中心处理')}>
          <Button disabled type="link">
            {t('添加争议证明')}
          </Button>
        </Tooltip>
      )}
      {operate.includes('OFFER') && (
        <OfferPartialRefund
          unit={unit}
          max={refundAmount}
          returnSn={returnSn}
          platform={platform}
          onSuccess={refreshList}
        />
      )}
    </>
  );
};
